import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Editor as TinymceEditor } from 'tinymce';

import type { BlockThemeConfig, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { sanitize } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import FormHtmlEditorFullComponent from 'webapp/components/form/html-editor-full';

import type BlockState from 'webapp/models-custom/block-state';
import type VisualizationText from 'webapp/models-custom/visualization/text';

interface Args {
	blockState: BlockState;
	themeData: BlockThemeConfig.BlockParsed;
	isUnselectable: boolean;
}

export default class BlockContentTextComponent extends Component<Args> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		void this.mmReportsService.loadFonts();
	}

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup() {
		return this.block.blockSetup;
	}

	get visualization(): VisualizationText {
		return this.blockSetup.visualization as VisualizationText;
	}

	// ----- when readonly ------

	get sanitizedText() {
		const text = this.visualization.shownText || '&nbsp;';
		return sanitize(text);
	}

	// ----- when editable ------

	editor?: TinymceEditor; // set from this.visualization.getEditorOptionsForComponent

	get editorOptions() {
		return this.visualization.getEditorOptionsForComponent(this);
	}

	@action
	onChange(text: string): void {
		const cleanText = text.trim(),
			currentSavedText = this.block.text;

		if (cleanText !== currentSavedText) {
			this.block.text = cleanText;
			this.blockSetup.saveBlock();
		}
	}

	@action
	setupEditedEvent() {
		this.eventManagerService.on(AppEvent.VIS_TEXT_EDITED, this, (eventData) => {
			if (eventData.id === this.block.id) {
				const editor = this.editor;
				if (editor) {
					const currentContent = editor.getContent();
					if (currentContent !== eventData.content) {
						editor.setContent(eventData.content);
					}
				}
			}
		});
	}

	@action
	teardownEditedEvent() {
		this.eventManagerService.off(AppEvent.VIS_TEXT_EDITED, this);
	}
	<template>
		<div class="block-content__main">
			<div
				class="bc__html-content user-html bgTheme-{{@themeData.bgTheme}}
					colorTheme-{{@themeData.themeId}}"
				{{didInsert this.setupEditedEvent}}
				{{willDestroy this.teardownEditedEvent}}
			>

				{{#if @isUnselectable}}

					{{{this.sanitizedText}}}

					{{#if this.visualization.shownDate}}
						<small class="lastUpdate">Note from {{this.visualization.shownDate}}</small>
					{{/if}}

				{{else}}

					<FormHtmlEditorFullComponent
						@text={{this.visualization.shownText}}
						@onChange={{this.onChange}}
						@options={{this.editorOptions}}
					/>

				{{/if}}
			</div>

		</div>
	</template>
}
